import React from "react";
import { Link } from "react-router-dom";

import SEO from "../../seo/SEO"

import {
  ArrowRight,
  CheckCircle2,
  ShoppingCart,
  Store,
  CreditCard,
  LayoutDashboard,
  Boxes,
  Smartphone,
  Search,
  Users,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";

import "./EcommerceDevelopment.css";


/* =========================================================
   SERVICES
========================================================= */

const services = [
  {
    icon: <ShoppingCart size={28} />,
    title: "Custom Ecommerce Development",
    description:
      "Custom B2B and B2C ecommerce websites designed around your products, customers, pricing, ordering and business workflows.",
  },

  {
    icon: <Store size={28} />,
    title: "B2B & B2C Ecommerce Platforms",
    description:
      "Online stores, dealer portals and multi-vendor ecommerce platforms with flexible pricing, customer groups and ordering workflows.",
  },

  {
    icon: <CreditCard size={28} />,
    title: "Payment Gateway Integration",
    description:
      "Secure online payments using Razorpay, PayU, Stripe, UPI, cards, wallets and other supported payment gateways.",
  },

  {
    icon: <Boxes size={28} />,
    title: "Product, Inventory & Order Management",
    description:
      "Manage products, categories, pricing, stock, customer orders, order status and fulfilment from one administrative system.",
  },

  {
    icon: <LayoutDashboard size={28} />,
    title: "Ecommerce Admin & MIS Dashboard",
    description:
      "Track orders, customers, products, payments, sales performance and operational information through business dashboards.",
  },

  {
    icon: <Smartphone size={28} />,
    title: "Ecommerce Mobile Applications",
    description:
      "Android and cross-platform ecommerce applications connected with your catalogue, customer accounts, orders and backend systems.",
  },

  {
    icon: <Users size={28} />,
    title: "CRM & Customer Integration",
    description:
      "Connect ecommerce with CRM, customer history, enquiries, sales follow-ups and customer-service workflows.",
  },

  {
    icon: <Search size={28} />,
    title: "SEO-Ready Ecommerce Development",
    description:
      "Search-friendly site structure, product URLs, metadata, performance optimisation and technical SEO foundations.",
  },

  {
    icon: <RefreshCcw size={28} />,
    title: "Maintenance & Business Support",
    description:
      "Ongoing ecommerce maintenance, enhancements, cloud support, integrations and business-process improvements after launch.",
  },
];


/* =========================================================
   TECHNOLOGY STACK
========================================================= */

const techStack = [
  "React.js",
  "Next.js",
  "FastAPI",
  "Node.js",
  "PostgreSQL",
  "MongoDB",
  "Docker",
  "AWS",
  "Redis",
  "React Native",
];


/* =========================================================
   FAQ
========================================================= */

const faqs = [
  {
    question: "How much does ecommerce website development cost?",
    answer:
      "The cost depends on catalogue size, design, payment gateway, shipping, CRM or ERP integrations, mobile application requirements and custom workflows. We evaluate the requirement before providing a project estimate.",
  },

  {
    question: "Can you build both B2B and B2C ecommerce platforms?",
    answer:
      "Yes. Bairagi Consultants develops B2C online stores as well as B2B ecommerce systems with dealer pricing, customer groups, bulk ordering and business-specific workflows.",
  },

  {
    question: "Can payment gateways be integrated?",
    answer:
      "Yes. We can integrate suitable payment gateways such as Razorpay, PayU, Stripe, UPI and other supported providers based on the project and business requirements.",
  },

  {
    question: "Can ecommerce be connected with CRM or MIS?",
    answer:
      "Yes. Ecommerce systems can be connected with customer CRM, sales workflows, order management, reporting dashboards and other business applications.",
  },

  {
    question: "Do you also develop ecommerce mobile apps?",
    answer:
      "Yes. We develop Android and cross-platform mobile applications that can share catalogue, customer, order and backend data with the ecommerce platform.",
  },

  {
    question: "Do you provide support after launch?",
    answer:
      "Yes. Maintenance, enhancements, cloud support, integrations and ongoing technical support can be provided after deployment.",
  },
];


/* =========================================================
   COMPONENT
========================================================= */

export default function EcommerceDevelopment() {

  /* =======================================================
     CANONICAL
  ======================================================= */

  const canonicalUrl =
    "https://www.bairagi-consultants.com/ecommerce-development";


  /* =======================================================
     SERVICE SCHEMA
  ======================================================= */

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",

    name: "Ecommerce Development Services",

    provider: {
      "@type": "Organization",
      name: "Bairagi Consultants",
      url: "https://www.bairagi-consultants.com",
    },

    serviceType: "Custom Ecommerce Development",

    areaServed: {
      "@type": "Country",
      name: "India",
    },

    url: canonicalUrl,

    description:
      "Custom B2B and B2C ecommerce development including online stores, payment gateway integration, product catalogue, order management, CRM integration, mobile apps and business dashboards.",
  };


  /* =======================================================
     FAQ SCHEMA
  ======================================================= */

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",

    mainEntity: faqs.map((faq) => ({
      "@type": "Question",

      name: faq.question,

      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };


  /* =======================================================
     ALL SCHEMAS
  ======================================================= */

  const ecommerceSchema = [
    serviceSchema,
    faqSchema,
  ];


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="Ecommerce Development Company in India | Bairagi Consultants"
        description="Custom ecommerce development company in India for B2B and B2C businesses. Build online stores, product catalogues, payment gateways, order management, CRM integrations and ecommerce mobile apps."
        path="/ecommerce-development"
        schema={ecommerceSchema}
      />


      <div className="ecommerce-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="ecommerce-hero ecommerce-container">

          <div className="ecommerce-hero-content">

            <span className="ecommerce-badge">
              Ecommerce Website & Software Development
            </span>

            <h1 className="ecommerce-title">
              Ecommerce Development
              <span> Company </span>
              <br />
              for Growing Businesses
            </h1>

            <p className="ecommerce-description">
              Build a secure and scalable B2B or B2C ecommerce
              platform with product catalogue, online payments,
              order management, CRM integration, MIS dashboards
              and mobile support.
            </p>


            <div className="ecommerce-actions">

              <Link
                to="/contact?service=ecommerce"
                className="ecommerce-primary-btn"
              >
                Get Project Estimate
                <ArrowRight size={18} />
              </Link>

              <Link
                to="/case-studies"
                className="ecommerce-secondary-btn"
              >
                View Case Studies
              </Link>

            </div>


            <div className="ecommerce-trust-points">

              <span>
                <CheckCircle2 size={18} />
                Custom Development
              </span>

              <span>
                <CheckCircle2 size={18} />
                Payment Integration
              </span>

              <span>
                <CheckCircle2 size={18} />
                Post-Launch Support
              </span>

            </div>

          </div>


          <div className="ecommerce-hero-card">

            <img
              src="https://images.unsplash.com/photo-1556740749-887f6717d7e4?q=80&w=1400&auto=format&fit=crop"
              alt="Ecommerce website and online shopping platform development"
            />

          </div>

        </section>


        {/* =================================================
            BUSINESS VALUE
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Turn Your Products Into
              <br />
              A Digital Sales Channel
            </h2>

            <p>
              We help businesses move beyond a basic online
              catalogue by creating ecommerce systems that
              support customer acquisition, ordering, payments,
              operations and repeat sales.
            </p>

          </div>


          <div className="ecommerce-services-grid">

            <div className="ecommerce-service-card">

              <ShoppingCart size={28} />

              <h3>
                Sell Online 24×7
              </h3>

              <p>
                Allow customers to discover products,
                place orders and make payments from
                anywhere.
              </p>

            </div>


            <div className="ecommerce-service-card">

              <LayoutDashboard size={28} />

              <h3>
                Control Business Operations
              </h3>

              <p>
                Manage products, orders, payments,
                customers and business reports from
                a central system.
              </p>

            </div>


            <div className="ecommerce-service-card">

              <ShieldCheck size={28} />

              <h3>
                Build for Future Growth
              </h3>

              <p>
                Start with the required capabilities
                and extend the platform as your
                business grows.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            SERVICES
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Ecommerce Development
              <br />
              Services
            </h2>

            <p>
              End-to-end ecommerce software development
              covering storefronts, payments, operations,
              customer management, mobile commerce and
              business integrations.
            </p>

          </div>


          <div className="ecommerce-services-grid">

            {services.map((service, index) => (

              <div
                key={index}
                className="ecommerce-service-card"
              >

                {service.icon}

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            DEVELOPMENT PROCESS
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Our Ecommerce Development Process
            </h2>

            <p>
              We first understand the business requirement
              and demonstrate the proposed solution before
              moving into full implementation.
            </p>

          </div>


          <div className="ecommerce-services-grid">

            {[
              [
                "01",
                "Business Discovery",
                "Understand products, customers, ordering process and business goals.",
              ],

              [
                "02",
                "Solution & Demo",
                "Define the solution and demonstrate the proposed customer and administration experience.",
              ],

              [
                "03",
                "Scope & Proposal",
                "Freeze project scope, integrations, milestones, timeline and commercial terms.",
              ],

              [
                "04",
                "Development",
                "Build and integrate the approved ecommerce modules and business workflows.",
              ],

              [
                "05",
                "Testing & Launch",
                "Validate checkout, payment, order, mobile and operational workflows before deployment.",
              ],

              [
                "06",
                "Support & Growth",
                "Maintain, enhance and integrate additional CRM, mobile, analytics or AI capabilities.",
              ],
            ].map(([number, title, description]) => (

              <div
                key={number}
                className="ecommerce-service-card"
              >

                <span className="ecommerce-process-number">
                  {number}
                </span>

                <h3>
                  {title}
                </h3>

                <p>
                  {description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            TECHNOLOGY
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Ecommerce Technology Stack
            </h2>

            <p>
              We select suitable frontend, backend,
              database, cloud and mobile technologies
              according to the scale and requirements
              of the project.
            </p>

          </div>


          <div className="ecommerce-tech-stack">

            {techStack.map((tech) => (

              <span
                key={tech}
                className="ecommerce-tech-tag"
              >
                {tech}
              </span>

            ))}

          </div>

        </section>


        {/* =================================================
            CASE STUDY
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-case-study">

            <span>
              Ecommerce Case Study
            </span>

            <h2>
              Dreamland Bites
            </h2>

            <p>
              Bairagi Consultants developed an ecommerce
              solution for a bakery business including
              product catalogue management, customer
              ordering, COD workflow, OTP verification,
              invoice generation and backend order
              administration.
            </p>

            <Link
              to="/case-studies"
              className="ecommerce-secondary-btn"
            >
              Explore Case Studies
              <ArrowRight size={18} />
            </Link>

          </div>

        </section>


        {/* =================================================
            WHY CHOOSE US
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Why Choose Bairagi Consultants
              for Ecommerce Development?
            </h2>

          </div>


          <div className="ecommerce-services-grid">

            <div className="ecommerce-service-card">

              <CheckCircle2 size={28} />

              <h3>
                Business-Focused Development
              </h3>

              <p>
                We design ecommerce around the
                client's operational and sales
                requirements rather than simply
                building web pages.
              </p>

            </div>


            <div className="ecommerce-service-card">

              <CheckCircle2 size={28} />

              <h3>
                Integrated Solutions
              </h3>

              <p>
                Ecommerce can be connected with CRM,
                MIS, mobile applications, customer
                communication and other business systems.
              </p>

            </div>


            <div className="ecommerce-service-card">

              <CheckCircle2 size={28} />

              <h3>
                Long-Term Technology Support
              </h3>

              <p>
                We support enhancements, integrations
                and platform evolution as the business
                grows.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FAQ
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-section-header">

            <h2>
              Ecommerce Development FAQs
            </h2>

          </div>


          <div className="ecommerce-faq-list">

            {faqs.map((faq, index) => (

              <div
                key={index}
                className="ecommerce-faq-item"
              >

                <h3>
                  {faq.question}
                </h3>

                <p>
                  {faq.answer}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="ecommerce-section ecommerce-container">

          <div className="ecommerce-cta">

            <h2>
              Planning an Ecommerce Project?
            </h2>

            <p>
              Tell us about your business, products,
              required features and expected launch.
              We will review the requirement and
              discuss the suitable implementation
              approach with you.
            </p>

            <Link
              to="/contact?service=ecommerce"
              className="ecommerce-primary-btn"
            >
              Get Ecommerce Project Estimate
              <ArrowRight size={18} />
            </Link>

          </div>

        </section>

      </div>
    </>
  );
}